import { getIdToken } from './firebase';
import { TASKS_CHANGED_EVENT } from './taskEvents';
import { groupHeaderFor, noteActionResponse, isContentSave } from './actionTracking';
import { noteContentRev, withContentRev, StaleContentError, STALE_CONTENT_EVENT } from './contentRevs';

const API_BASE = '/api';

/** Timezone override set from PreferencesContext. Module-level so non-React code can read it. */
let _timezoneOverride: string | null = null;

/** Called by PreferencesContext to push the user's timezone preference into the API layer. */
export function setTimezoneOverride(tz: string | null) {
  _timezoneOverride = tz;
}

/** Get the user's IANA timezone string (e.g. 'America/New_York'). */
function getUserTimezone(): string {
  if (_timezoneOverride && _timezoneOverride !== 'auto') {
    return _timezoneOverride;
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'X-Timezone': getUserTimezone(),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/** Auth header only (no Content-Type) — used for multipart/form-data uploads */
async function authOnlyHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  const headers: HeadersInit = {
    'X-Timezone': getUserTimezone(),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  noteContentRev(path, data);
  return data;
}

/**
 * A write: carries any armed action group and reports its recorded action
 * (#278), and sends a document's revision with its autosaves (#288).
 */
interface WriteOptions {
  /** False when the caller handles a stale 409 itself (excerpt write-back) instead of reloading the page. */
  announceStale?: boolean;
}

async function write(method: string, path: string, body?: unknown, opts: WriteOptions = {}): Promise<Response> {
  const sent = withContentRev(method, path, body);
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { ...(await authHeaders()), ...groupHeaderFor(method, body) },
    body: sent === undefined ? undefined : JSON.stringify(sent),
  });
  if (res.status === 409 && sent !== body) {
    if (opts.announceStale !== false) window.dispatchEvent(new CustomEvent(STALE_CONTENT_EVENT, { detail: { path } }));
    throw new StaleContentError();
  }
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  noteActionResponse(method, res);
  if (path === '/tasks' || path.startsWith('/tasks/') || path.startsWith('/tasks?')) {
    window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT));
  }
  return res;
}


/** Parse a write's JSON response, noting a document's new revision. */
/** Fired after a document autosave succeeds; excerpts (#277) refresh what was shown from it. */
export const CONTENT_SAVED_EVENT = 'dossier:content-saved';

/**
 * Autosaves of one document go one at a time (#288). The editor saves on every
 * keystroke, so without this a second save leaves before the first returns,
 * carries the same base_content_rev, and is refused as stale - reloading the
 * page mid-typing. Saves that pile up behind the one in flight merge into a
 * single follow-up (each is the whole body, so the newest wins), which also
 * stops an older body from landing after a newer one.
 */
interface QueuedSave {
  body: Record<string, unknown>;
  quiet: boolean;
  waiters: Array<{ resolve: (v: unknown) => void; reject: (e: unknown) => void }>;
}
const saveLanes = new Map<string, { queued: QueuedSave | null }>();

function queueContentSave<T>(path: string, body: Record<string, unknown>, opts?: WriteOptions): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const waiter = { resolve: resolve as (v: unknown) => void, reject };
    const quiet = opts?.announceStale === false;
    const lane = saveLanes.get(path);
    if (lane) {
      if (lane.queued) {
        lane.queued.body = { ...lane.queued.body, ...body };
        lane.queued.quiet &&= quiet;
        lane.queued.waiters.push(waiter);
      } else {
        lane.queued = { body, quiet, waiters: [waiter] };
      }
      return;
    }
    const fresh = { queued: { body, quiet, waiters: [waiter] } as QueuedSave | null };
    saveLanes.set(path, fresh);
    void drainSaves(path, fresh);
  });
}

async function drainSaves(path: string, lane: { queued: QueuedSave | null }): Promise<void> {
  while (lane.queued) {
    const job = lane.queued;
    lane.queued = null;
    try {
      const data = await sendJson('PATCH', path, job.body, { announceStale: !job.quiet });
      for (const w of job.waiters) w.resolve(data);
    } catch (err) {
      for (const w of job.waiters) w.reject(err);
      // A stale document stays stale: fail what queued behind it rather than send it.
      const behind = lane.queued as QueuedSave | null; // set by callers while we awaited
      if (err instanceof StaleContentError && behind) {
        for (const w of behind.waiters) w.reject(new StaleContentError());
        lane.queued = null;
      }
    }
  }
  saveLanes.delete(path);
}

async function writeJson<T>(method: string, path: string, body: unknown, opts?: WriteOptions): Promise<T> {
  if (isContentSave(method, body)) return queueContentSave<T>(path, body as Record<string, unknown>, opts);
  return sendJson<T>(method, path, body, opts);
}

async function sendJson<T>(method: string, path: string, body: unknown, opts?: WriteOptions): Promise<T> {
  const data = await (await write(method, path, body, opts)).json();
  noteContentRev(path, data);
  if (isContentSave(method, body)) window.dispatchEvent(new CustomEvent(CONTENT_SAVED_EVENT, { detail: { path } }));
  return data as T;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  return writeJson<T>('POST', path, body);
}

export async function apiPatch<T>(path: string, body: unknown, opts?: WriteOptions): Promise<T> {
  return writeJson<T>('PATCH', path, body, opts);
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  return writeJson<T>('PUT', path, body);
}

export async function apiDelete(path: string): Promise<void> {
  await write('DELETE', path);
}

/** DELETE whose route answers with a JSON body. */
export async function apiDeleteJson<T>(path: string): Promise<T> {
  return writeJson<T>('DELETE', path, undefined);
}

/** POST that hands back the status and parsed body instead of throwing, for routes whose refusals carry detail (409s). */
export async function apiPostRaw<T>(path: string, body: unknown = {}): Promise<{ status: number; body: T }> {
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', headers: await authHeaders(), body: JSON.stringify(body) });
  return { status: res.status, body: (await res.json().catch(() => ({}))) as T };
}

/**
 * Upload a file via multipart/form-data.
 * Do NOT set Content-Type — the browser sets it with the boundary automatically.
 */
export async function apiUpload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...(await authOnlyHeaders()), ...groupHeaderFor('POST', formData) },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error: ${res.status} ${res.statusText}`);
  }
  noteActionResponse('POST', res);
  return res.json();
}

/**
 * Get the authenticated download URL for a file.
 * Returns a blob URL that can be used in <a href> or window.open.
 */
export async function apiDownloadUrl(path: string): Promise<string> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: await authOnlyHeaders(),
  });
  if (!res.ok) {
    throw new Error(`API error: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export { TASKS_CHANGED_EVENT };
